// SEQ-180 dev-only fixtures for the P&L analytics page. `pnl_analytics_query`
// (supabase/migrations/20260908000500_pnl_analytics_query.sql) has not been
// applied to any database yet, so `usePnlAnalytics` falls back to these
// shapes in development only, never in production (see usePnlAnalytics.ts).
// Every project name below is fictional ("Fixture ..."), on purpose, so
// nobody mistakes it for a real client's numbers on a screen share.
//
// CONTRACT CHANGE (round 3 addendum): the RPC no longer offers a combined
// project+month grain, only project | month | quarter | calendar_year |
// financial_year | builder | status. This module keeps one internal
// per-project-per-month synthetic dataset (never exposed directly) and
// derives three separate response shapes from it, matching the three calls
// usePnlAnalytics.ts actually makes: the page's project-grain call, its
// month-grain call, and the trend widget's on-demand single-project
// month-grain call.

import type { PnlAnalyticsMeta, PnlAnalyticsResponse, PnlAnalyticsRow } from "../../lib/pnl/analyticsClient";
import { PNL_METRIC_KEYS } from "../../lib/pnl/pnlAnalytics";

interface FixtureProject {
  id: string;
  name: string;
  /** Base monthly revenue, dollars. */
  baseRevenue: number;
  /** Cost as a fraction of revenue, before overtime/patching noise. */
  costRatio: number;
  /** Overtime as a fraction of revenue. */
  overtimeRatio: number;
  /** Patching as a fraction of revenue. */
  patchingRatio: number;
  /**
   * SEQ-182. Base ordinary (non-overtime) crew hours for a typical fixture
   * month, illustrative only. Overtime hours are not a separate input: they
   * are derived below from the project's own overtime dollar figure at an
   * assumed blended rate, so the fixture's overtime cost and overtime hours
   * never disagree with each other.
   */
  baseOrdinaryHours: number;
}

/** Assumed blended overtime hourly rate, fixture data only, used to turn the existing overtime-dollars ratio into a plausible overtime-hours figure. */
const FIXTURE_OVERTIME_RATE_PER_HOUR = 75;

const FIXTURE_PROJECTS: FixtureProject[] = [
  { id: "fixture-project-1", name: "Fixture Quay", baseRevenue: 420000, costRatio: 0.78, overtimeRatio: 0.04, patchingRatio: 0.01, baseOrdinaryHours: 1100 },
  { id: "fixture-project-2", name: "Fixture Tower", baseRevenue: 610000, costRatio: 0.9, overtimeRatio: 0.09, patchingRatio: 0.025, baseOrdinaryHours: 1600 },
  { id: "fixture-project-3", name: "Fixture Reserve", baseRevenue: 305000, costRatio: 0.7, overtimeRatio: 0.02, patchingRatio: 0.008, baseOrdinaryHours: 800 },
  { id: "fixture-project-4", name: "Fixture Landing", baseRevenue: 275000, costRatio: 1.05, overtimeRatio: 0.12, patchingRatio: 0.035, baseOrdinaryHours: 720 },
  { id: "fixture-project-5", name: "Fixture Yards", baseRevenue: 190000, costRatio: 0.82, overtimeRatio: 0.05, patchingRatio: 0.015, baseOrdinaryHours: 500 },
  { id: "fixture-project-6", name: "Fixture Point", baseRevenue: 530000, costRatio: 0.74, overtimeRatio: 0.03, patchingRatio: 0.006, baseOrdinaryHours: 1400 },
];

const FIXTURE_MONTHS = ["2026-01-01", "2026-02-01", "2026-03-01", "2026-04-01", "2026-05-01", "2026-06-01"];

// Round 5: every PnlAnalyticsRow now requires organizationId (security
// re-audit, buckets scoped per organisation for multi-org callers). One
// fixture org id, since production is single-org and fixture mode never
// simulates a multi-org caller.
const FIXTURE_ORGANIZATION_ID = "33333333-3333-3333-3333-333333333333";

interface FixtureFact {
  projectId: string;
  projectName: string;
  periodMonth: string;
  revenue: number;
  cost: number;
  profit: number;
  overtime: number;
  patching: number;
  /** SEQ-182. Ordinary (non-overtime) crew hours for this project-month, fixture only. */
  ordinaryHours: number;
  /** SEQ-182. Overtime hours for this project-month, derived from `overtime` dollars at FIXTURE_OVERTIME_RATE_PER_HOUR so cost and hours agree. */
  overtimeHours: number;
}

/** Internal per-project-per-month synthetic dataset. Never returned to a caller directly: the real RPC never returns this grain any more. */
function buildFixtureFacts(): FixtureFact[] {
  const facts: FixtureFact[] = [];

  FIXTURE_PROJECTS.forEach((project, projectIdx) => {
    FIXTURE_MONTHS.forEach((periodMonth, monthIdx) => {
      // Deterministic month-to-month wobble so the trend line has shape
      // without needing a random number generator (fixtures must be stable
      // across renders and across test runs).
      const wobble = 1 + 0.08 * Math.sin(projectIdx + monthIdx * 0.9);
      const revenue = Math.round(project.baseRevenue * wobble);
      const cost = Math.round(revenue * project.costRatio);
      const overtime = Math.round(revenue * project.overtimeRatio);
      const patching = Math.round(revenue * project.patchingRatio);
      const profit = revenue - cost;
      const ordinaryHours = Math.round(project.baseOrdinaryHours * wobble);
      const overtimeHours = Math.round(overtime / FIXTURE_OVERTIME_RATE_PER_HOUR);

      facts.push({
        projectId: project.id,
        projectName: project.name,
        periodMonth,
        revenue,
        cost,
        profit,
        overtime,
        patching,
        ordinaryHours,
        overtimeHours,
      });
    });
  });

  return facts;
}

const FIXTURE_FACTS = buildFixtureFacts();

function metricsOf(fact: {
  revenue: number;
  cost: number;
  profit: number;
  overtime: number;
  patching: number;
  ordinaryHours: number;
  overtimeHours: number;
}) {
  const totalLabourHours = fact.ordinaryHours + fact.overtimeHours;
  return {
    [PNL_METRIC_KEYS.calculated.totalIncome]: fact.revenue,
    [PNL_METRIC_KEYS.calculated.totalCost]: fact.cost,
    [PNL_METRIC_KEYS.calculated.netProfit]: fact.profit,
    [PNL_METRIC_KEYS.labour.overtime]: fact.overtime,
    [PNL_METRIC_KEYS.expense.patching]: fact.patching,
    // SEQ-182: the six new registry keys. calculated.total_labour_hours is
    // included for parity with what the real RPC returns (it computes this
    // server side); every client-side consumer still recomputes its own
    // total from the raw ordinary/overtime figures rather than trusting
    // this field, matching plan section 6.1.
    [PNL_METRIC_KEYS.labour.ordinaryHours]: fact.ordinaryHours,
    [PNL_METRIC_KEYS.labour.overtimeHours]: fact.overtimeHours,
    [PNL_METRIC_KEYS.calculated.totalLabourHours]: totalLabourHours,
    [PNL_METRIC_KEYS.calculated.overtimeHoursPct]: totalLabourHours > 0 ? (fact.overtimeHours / totalLabourHours) * 100 : null,
  };
}

const FIXTURE_META: PnlAnalyticsMeta = {
  unmappedCategories: ["Fixture Legacy Category A", "Fixture Legacy Category B"],
  unmappedLocations: 14,
  payrollAsAt: "2026-09-07T20:00:00Z",
  generatedAt: "2026-09-08T02:00:00Z",
};

/** groupBy: "project": one row per fixture project, summed across every fixture month. */
function buildProjectGrainRows(): PnlAnalyticsRow[] {
  return FIXTURE_PROJECTS.map((project) => {
    const facts = FIXTURE_FACTS.filter((f) => f.projectId === project.id);
    const totals = facts.reduce(
      (acc, f) => ({
        revenue: acc.revenue + f.revenue,
        cost: acc.cost + f.cost,
        profit: acc.profit + f.profit,
        overtime: acc.overtime + f.overtime,
        patching: acc.patching + f.patching,
        ordinaryHours: acc.ordinaryHours + f.ordinaryHours,
        overtimeHours: acc.overtimeHours + f.overtimeHours,
      }),
      { revenue: 0, cost: 0, profit: 0, overtime: 0, patching: 0, ordinaryHours: 0, overtimeHours: 0 },
    );
    return {
      groupKey: project.id,
      groupLabel: project.name,
      metricsAsAt: {},
      projectId: project.id,
      organizationId: FIXTURE_ORGANIZATION_ID,
      periodMonth: null,
      metrics: metricsOf(totals),
    };
  });
}

/** groupBy: "month": one row per calendar month, summed across every fixture project. */
function buildMonthGrainRows(): PnlAnalyticsRow[] {
  return FIXTURE_MONTHS.map((periodMonth) => {
    const facts = FIXTURE_FACTS.filter((f) => f.periodMonth === periodMonth);
    const totals = facts.reduce(
      (acc, f) => ({
        revenue: acc.revenue + f.revenue,
        cost: acc.cost + f.cost,
        profit: acc.profit + f.profit,
        overtime: acc.overtime + f.overtime,
        patching: acc.patching + f.patching,
        ordinaryHours: acc.ordinaryHours + f.ordinaryHours,
        overtimeHours: acc.overtimeHours + f.overtimeHours,
      }),
      { revenue: 0, cost: 0, profit: 0, overtime: 0, patching: 0, ordinaryHours: 0, overtimeHours: 0 },
    );
    return {
      groupKey: periodMonth.slice(0, 7),
      groupLabel: periodMonth.slice(0, 7),
      metricsAsAt: {},
      projectId: null,
      organizationId: FIXTURE_ORGANIZATION_ID,
      periodMonth,
      metrics: metricsOf(totals),
    };
  });
}

export const FIXTURE_PNL_ANALYTICS_PROJECT_RESPONSE: PnlAnalyticsResponse = {
  rows: buildProjectGrainRows(),
  meta: FIXTURE_META,
};

export const FIXTURE_PNL_ANALYTICS_MONTH_RESPONSE: PnlAnalyticsResponse = {
  rows: buildMonthGrainRows(),
  meta: FIXTURE_META,
};

/**
 * groupBy: "month", projectIds: [projectId]: one row per calendar month for
 * a single project, the shape the trend widget's on-demand drill call needs.
 * Empty rows for a projectId that matches no fixture project (e.g. a real
 * production id typed in while the fixture happens to be active).
 */
export function buildFixtureProjectTrendResponse(projectId: string): PnlAnalyticsResponse {
  const rows: PnlAnalyticsRow[] = [];
  for (const periodMonth of FIXTURE_MONTHS) {
    const fact = FIXTURE_FACTS.find((f) => f.projectId === projectId && f.periodMonth === periodMonth);
    if (!fact) continue;
    rows.push({
      groupKey: periodMonth.slice(0, 7),
      groupLabel: periodMonth.slice(0, 7),
      metricsAsAt: {},
      projectId: null,
      organizationId: FIXTURE_ORGANIZATION_ID,
      periodMonth,
      metrics: metricsOf(fact),
    });
  }

  return { rows, meta: FIXTURE_META };
}

/** projectId -> display name, for consumers that have no matching claims_projects row (fixture mode only). */
export const FIXTURE_PROJECT_NAMES: Record<string, string> = Object.fromEntries(
  FIXTURE_PROJECTS.map((p) => [p.id, p.name]),
);
